// @ts-nocheck
import React, { useState, useCallback } from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import {
  Search,
  Calendar,
  Clock,
  Users,
  Sparkles,
  ArrowRight,
  Dribbble,
  Palette,
  FlaskConical,
  Music,
  Tent,
} from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';

interface QuickSearchSectionProps {
  sectionId: string;
  sectionData?: any[];
  [key: string]: any;
}

const categories = [
  { value: "sports", label: "Sports", Icon: Dribbble },
  { value: "arts", label: "Arts", Icon: Palette },
  { value: "stem", label: "STEM", Icon: FlaskConical },
  { value: "music", label: "Music", Icon: Music },
  { value: "dance", label: "Dance", Icon: Users },
  { value: "camp", label: "Camps", Icon: Tent },
];

const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const quickFilters = [
  { label: "Weekday Afternoons", days: [1, 2, 3, 4, 5], timeRange: [12, 17] },
  { label: "Saturday Mornings", days: [6], timeRange: [6, 12] },
  { label: "Sunday Afternoons", days: [0], timeRange: [12, 17] },
  { label: "After School", days: [1, 2, 3, 4, 5], timeRange: [15, 18] },
];

const formatHour = (hour: number) => {
  if (hour === 0) return "12 AM";
  if (hour === 12) return "12 PM";
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
};

const QuickSearchSection: React.FC<QuickSearchSectionProps> = ({
  sectionId,
  sectionData,
  ...extraProps
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [timeRange, setTimeRange] = useState<[number, number]>([6, 21]);
  const [age, setAge] = useState<string>("");

  const toggleDay = (dayIndex: number) => {
    setSelectedDays(prev =>
      prev.includes(dayIndex)
        ? prev.filter(d => d !== dayIndex)
        : [...prev, dayIndex]
    );
  };

  const handleQuickFilter = (quick: typeof quickFilters[0]) => {
    setSelectedDays(quick.days);
    setTimeRange(quick.timeRange as [number, number]);
  };

  const hasFilters = selectedDays.length > 0 || timeRange[0] !== 6 || timeRange[1] !== 21 || age !== "";

  const clearFilters = () => {
    setSelectedDays([]);
    setTimeRange([6, 21]);
    setAge("");
  };

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const params = new URLSearchParams();
      if (searchQuery) params.set("q", searchQuery);
      if (selectedDays.length > 0) params.set("days", selectedDays.join(","));
      if (timeRange[0] !== 6 || timeRange[1] !== 21) {
        params.set("startTime", `${timeRange[0].toString().padStart(2, "0")}:00`);
        params.set("endTime", `${timeRange[1].toString().padStart(2, "0")}:00`);
      }
      if (age) {
        params.set("age", age);
      }
      if (typeof window !== 'undefined') {
        window.location.href = `/s?${params.toString()}`;
      }
    },
    [searchQuery, selectedDays, timeRange, age]
  );

  return (
    <section id={sectionId} className="bg-muted/30">
      <div className="container px-4 md:px-6 py-16 lg:py-24">
        <div className="text-center mb-10">
          <p className="pb-3 font-bold text-destructive text-sm tracking-wide uppercase">Quick Search</p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3">
            Find the <span className="text-primary underline underline-offset-4">Perfect</span> Activity
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto font-medium">
            Use these filters to narrow down activities that match your family's schedule.
          </p>
        </div>

        <div className="bg-background rounded-2xl border p-6 lg:p-10 text-left max-w-3xl mx-auto box-shadow-card">
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-primary" />
                  Which days work for you?
                </label>
                {hasFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-destructive hover:text-destructive" data-testid="button-clear-filters">
                    Clear all
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {dayNames.map((day, index) => (
                  <button
                    key={day}
                    onClick={() => toggleDay(index)}
                    className={`inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                      selectedDays.includes(index)
                        ? "bg-primary text-primary-foreground shadow-md"
                        : "border border-border bg-background hover:border-primary/50 hover:text-primary"
                    }`}
                    data-testid={`button-day-${day.toLowerCase()}`}
                  >
                    {day}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  className="text-xs font-semibold text-primary hover:underline"
                  onClick={() => setSelectedDays([1, 2, 3, 4, 5])}
                  data-testid="button-weekdays"
                >
                  Weekdays
                </button>
                <button
                  className="text-xs font-semibold text-primary hover:underline"
                  onClick={() => setSelectedDays([0, 6])}
                  data-testid="button-weekends"
                >
                  Weekends
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold flex items-center gap-2 mb-3">
                <Clock className="h-4 w-4 text-primary" />
                What time of day?
                <span className="text-muted-foreground font-normal">
                  ({formatHour(timeRange[0])} - {formatHour(timeRange[1])})
                </span>
              </label>
              <SliderPrimitive.Root
                className="relative flex w-full touch-none select-none items-center"
                value={timeRange}
                onValueChange={(val) => setTimeRange(val as [number, number])}
                min={6}
                max={21}
                step={1}
                data-testid="slider-time-range"
              >
                <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-secondary">
                  <SliderPrimitive.Range className="absolute h-full bg-primary" />
                </SliderPrimitive.Track>
                <SliderPrimitive.Thumb className="block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0" />
                <SliderPrimitive.Thumb className="block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0" />
              </SliderPrimitive.Root>
              <div className="flex justify-between text-xs text-muted-foreground mt-1 font-medium">
                <span>6 AM</span>
                <span>12 PM</span>
                <span>9 PM</span>
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold flex items-center gap-2 mb-3">
                <Users className="h-4 w-4 text-primary" />
                Child's age
              </label>
              <Input
                type="number"
                min={0}
                max={18}
                placeholder="Enter age (0-18)"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                className="w-full max-w-[200px] rounded-xl"
                data-testid="input-age"
              />
            </div>

            <div>
              <label className="text-sm font-semibold flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-primary" />
                Quick picks
              </label>
              <div className="flex flex-wrap gap-2">
                {quickFilters.map((quick) => (
                  <button
                    key={quick.label}
                    onClick={() => handleQuickFilter(quick)}
                    className="inline-flex items-center rounded-full border border-border bg-background px-4 py-2 text-sm font-medium hover:border-primary/50 hover:text-primary transition-colors duration-300"
                    data-testid={`quick-filter-${quick.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {quick.label}
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 pt-2">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search by name, organization, or keyword..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 rounded-full border-2 focus:border-primary"
                  data-testid="input-search-home"
                />
              </div>
              <button
                type="submit"
                className="servibe-btn servibe-btn-primary inline-flex items-center justify-center gap-2 px-8 py-3 text-sm whitespace-nowrap"
                data-testid="button-search-home"
              >
                <span>Search Activities</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>

            <div className="flex flex-wrap justify-center gap-3 pt-2">
              {categories.map((category) => (
                <a key={category.value} href={`/s?pub_category=${category.value}`}>
                  <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-medium hover:border-primary/50 hover:text-primary transition-colors duration-300 cursor-pointer" data-testid={`chip-category-${category.value}`}>
                    <category.Icon className="h-4 w-4" />
                    {category.label}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuickSearchSection;
